import {useThemeColors} from '@nfq/colors';

import {useLivePreview} from 'Application/useCases/useLivePreview';

import {getLinkData} from 'Application/utils/linkFieldUtils';
import {splitText} from 'UI/utils/helpers';

import type {Footer} from 'types/payload-types';

/**
 * A custom hook that provides live preview capabilities for the footer content.
 * This hook connects to the live preview system using the `useLivePreview` utility, enabling real-time updates
 * to the footer data while editing in a CMS or preview environment. It initializes with the provided `footerData`
 * and listens for changes using a unique data key and a specified nesting depth for deep object updates.
 * The hook abstracts away the setup required to watch for footer changes, allowing components to reactively consume
 * updated data without reloading the page.
 *
 * @param footerData The initial `Footer` object containing structured content such as links, legal text, or branding.
 * @returns An object with a reactive `footer` property reflecting the current footer state.
 *
 * @example
 * ```tsx
 * const { footer } = useFooter(initialFooterData);
 * return <FooterComponent data={footer} />;
 * ```
 */
export const useFooter = (footerData: Footer) => {
    const colors = useThemeColors();
    const footer = useLivePreview<Footer>({
        dataKey: 'Footer',
        depth: 2,
        initialData: footerData
    });
    const year = new Date().getFullYear();
    const {href: mnetHref, label: mnetLabel, rel: mnetRel, target: mnetTarget} = getLinkData(footer.partner.mnetLink);
    const {href: swmHref, label: swmLabel, rel: swmRel, target: swmTarget} = getLinkData(footer.partner.swmLink);
    const {href: telHref, label: telLabel, rel: telRel, target: telTarget} = getLinkData(footer.partner.telekomLink);

    return {
        colors,
        copyright: footer.endnotes?.copyright,
        footerLinks: footer.endnotes?.links ?? [],
        headline: splitText(footer.headline),
        mnetHref,
        mnetLabel,
        mnetRel,
        mnetTarget,
        overline: footer.overline,
        swmHref,
        swmLabel,
        swmRel,
        swmTarget,
        telHref,
        telLabel,
        telRel,
        telTarget,
        year
    };
};